import { isIndustrySlug } from './industryCards';
import { MetricIdentity, identifiersNamedBy } from './metricIdentifiers';
import { isProductMetricKey, productMetricKeysFor } from './productReadiness';

// The join from a shipped Customer 360 tile to the catalogue metric it reports. Same contract as
// `registry/metricIdentifiers.ts` — `identifier` is the catalogue Metric ID VERBATIM, and the
// backend resolves it to an environment's numeric Metabase card id — but keyed on the C360 card
// registry, which is a different card set.
//
// Scope: the 54 Banking/BFSI Phase-1 `C-*` metrics of `design/banking-scope.md`, plus the ten
// Insurance Phase-1 `C-INS-PR-*` product-readiness metrics — the Product Readiness card is ONE
// selector over every industry's products, so it declares them all and the tenant's industry picks
// the subset the dropdown offers. The three Phase-2 ids (`C-REV-005`, `C-REV-006`, `C-HYB-010`)
// appear nowhere, and no `O-*` id appears at all — those are Layer 1 portfolio metrics, measured
// over the customer base rather than over one customer.
//
// `{ kind: 'unmapped' }` is load-bearing here in a way it is not on Layer 1. The frame draws tiles
// the catalogue has no metric for (`figma-spec.md` § 7.3, `notes.md` § 5.3); the decision is that
// they render from the fixture and carry NO API binding, so they are never fetched and a live card
// reports them `notConfigured` rather than inventing a source. PLA-8828.
//
// Keyed by card key in `customer360Cards.ts` order, each card's tiles in its `metricKeys` order.
export const customer360MetricIdentities: Record<string, MetricIdentity[]> = {
  customer_details: [
    {
      key: 'customer_profile_attributes',
      label: 'Customer Profile Attributes',
      format: 'count',
      // Not a metric: the catalogue names this "Existing Customer Profile Attributes (Existing
      // customer detail section of the customer module)" and the card renders from the Customer
      // Management profile this page already fetches. Metabase question 21831 declares no
      // `customer_id`, so binding it here only ever produced a 502. PLA-8828.
      riseMeans: 'neutral',
      source: { kind: 'unmapped' }
    },
    {
      key: 'permissions_availability',
      label: 'Permissions & Availability',
      format: 'count',
      riseMeans: 'neutral',
      source: { kind: 'unmapped' }
    }
  ],
  holdings_customer_value: [
    {
      key: 'current_clv_band',
      label: 'Current CLV · Trailing 12M',
      format: 'currency',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'C-COM-003' }
    },
    {
      key: 'predicted_clv_band',
      label: 'Predicted CLV · Next 12M',
      format: 'currency',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'C-COM-004' }
    },
    {
      key: 'product_holding_count_distribution',
      label: 'Holdings by Product Family',
      format: 'count',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'C-COM-023' }
    }
  ],
  profile_summary: [
    {
      key: 'identity_confidence',
      label: 'Identity Confidence',
      format: 'percent',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'C-COM-001' }
    },
    {
      key: 'lifecycle_stage',
      label: 'Lifecycle Stage',
      format: 'count',
      riseMeans: 'neutral',
      source: { kind: 'metric', identifier: 'C-COM-002' }
    },
    {
      key: 'current_tier',
      label: 'Current Tier',
      format: 'count',
      // C-COM-025 states the tier as a word in `value_state` and carries no `metric_value`, so the
      // word IS the reading (`Synthetic Tier 2 (...)` for customer 4, 2026-09-30). PLA-8828.
      reading: 'categorical',
      riseMeans: 'neutral',
      source: { kind: 'metric', identifier: 'C-COM-025' }
    }
  ],
  customer_behaviour_profile: [
    {
      key: 'behavioural_momentum_score',
      label: 'Behavioral Momentum Score (BMS)',
      format: 'points',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'C-COM-005' }
    },
    {
      key: 'behavioural_deviation_score',
      label: 'Behavioral Deviation Score (BDS)',
      format: 'points',
      riseMeans: 'neutral',
      source: { kind: 'metric', identifier: 'C-COM-006' }
    },
    {
      key: 'disengagement_early_warning_score',
      label: 'Disengagement Early Warning Score',
      format: 'points',
      riseMeans: 'negative',
      source: { kind: 'metric', identifier: 'C-COM-007' }
    },
    {
      key: 'habit_strength_score',
      label: 'Habit Strength Score',
      format: 'points',
      riseMeans: 'positive',
      // Drawn as the card's fourth score tile, but it is `O-COM-010` — a PORTFOLIO metric on the
      // Layer 1 catalogue, with no `C-*` counterpart. Binding it to the `O-*` id would report the
      // whole customer base's habit strength on one customer's card, so it stays unmapped.
      source: { kind: 'unmapped' }
    }
  ],
  engagement_timeline: [
    {
      key: 'funnel_stage',
      // `C-COM-010` is an ordinal stage (`value_state: "stage_2"`), not a rate — as `percent` it
      // printed `2%`. No `MetricFormat` states an ordinal; `count` prints the position. PLA-8828.
      label: 'Customer Funnel Stage',
      format: 'count',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'C-COM-010' }
    },
    {
      key: 'last_engagement_date',
      label: 'Last Engagement',
      format: 'count',
      riseMeans: 'neutral',
      // The same shape as `C-COM-021`: `calculated` with the reading in `value_state` (a date,
      // `2026-06-29` on UAT `generic`) and no `metric_value` to format. PLA-8828.
      reading: 'categorical',
      source: { kind: 'metric', identifier: 'C-COM-008' }
    },
    {
      key: 'engagement_frequency',
      label: 'Engagements',
      format: 'count',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'C-COM-009' }
    },
    {
      key: 'days_since_last_engagement',
      label: 'Days Since Last Engagement',
      format: 'count',
      riseMeans: 'negative',
      source: { kind: 'unmapped' }
    }
  ],
  sentiment_expectations: [
    {
      key: 'sentiment_score',
      label: 'Sentiment Score',
      format: 'points',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'C-COM-015' }
    },
    {
      key: 'expectation_gap_score',
      label: 'Expectation Gap',
      format: 'points',
      riseMeans: 'negative',
      source: { kind: 'metric', identifier: 'C-COM-016' }
    }
  ],
  rewards_loyalty_activity: [
    {
      key: 'reward_sensitivity_score',
      label: 'Reward Sensitivity',
      format: 'percent',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'C-COM-013' }
    },
    {
      key: 'incentive_dependency_score',
      label: 'Incentive Dependency',
      format: 'percent',
      riseMeans: 'negative',
      source: { kind: 'metric', identifier: 'C-COM-014' }
    },
    {
      key: 'reward_redemption_rate',
      label: 'Reward Redemption',
      format: 'percent',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'C-COM-011' }
    },
    {
      key: 'points_balance_movement',
      label: 'Earn + Burn Ratio',
      format: 'ratio',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'C-COM-012' }
    },
    {
      key: 'rewards_issued',
      label: 'Rewards Issued',
      format: 'count',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'C-COM-011', component: 'entitlements_issued_count' }
    },
    {
      key: 'rewards_redeemed',
      label: 'Rewards Redeemed',
      format: 'count',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'C-COM-011', component: 'entitlements_redeemed_count' }
    },
    {
      key: 'rewards_expired',
      label: 'Rewards Expired',
      format: 'count',
      riseMeans: 'negative',
      source: { kind: 'metric', identifier: 'C-COM-011', component: 'entitlements_expired_count' }
    },
    {
      key: 'reversal_count',
      label: 'Reversals',
      format: 'count',
      riseMeans: 'negative',
      source: { kind: 'metric', identifier: 'C-REV-001' }
    },
    {
      key: 'upcoming_expiry_30d',
      label: 'Upcoming Expiry (30D)',
      format: 'count',
      riseMeans: 'negative',
      source: { kind: 'unmapped' }
    },
    {
      key: 'reversed_entitlement_summary',
      label: 'Reversed Value',
      format: 'currency',
      riseMeans: 'negative',
      source: { kind: 'metric', identifier: 'C-REV-003' }
    },
    {
      key: 'reversal_reason_profile',
      label: 'Reversal Rate',
      format: 'percent',
      riseMeans: 'negative',
      source: { kind: 'metric', identifier: 'C-REV-004' }
    },
    {
      key: 'reversal_timeline_events',
      label: 'Reversed Outcome',
      format: 'points',
      riseMeans: 'negative',
      source: { kind: 'metric', identifier: 'C-REV-002' }
    },
    {
      key: 'tier_progression',
      label: 'Tier Progression',
      format: 'percent',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'C-COM-026' }
    }
  ],
  data_confidence_supporting_evidence: [
    {
      key: 'evidence_pack_completeness',
      label: 'Evidence Pack Completeness',
      format: 'percent',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'C-COM-022' }
    }
  ],
  risk_unusual_activity: [
    {
      key: 'fraud_anomaly_score',
      label: 'Fraud / Anomaly Score (FAS)',
      format: 'points',
      riseMeans: 'negative',
      source: { kind: 'metric', identifier: 'C-COM-017' }
    },
    {
      key: 'affected_events_30d',
      label: 'Affected Events (30D)',
      format: 'count',
      riseMeans: 'negative',
      source: { kind: 'unmapped' }
    },
    {
      key: 'latest_event',
      label: 'Latest Event',
      format: 'count',
      riseMeans: 'neutral',
      source: { kind: 'unmapped' }
    },
    {
      key: 'highest_severity_30d',
      label: 'Highest Severity (30D)',
      format: 'count',
      riseMeans: 'negative',
      source: { kind: 'unmapped' }
    },
    {
      key: 'impacted_metrics_decisions',
      label: 'Impacted Metrics / Decisions',
      format: 'count',
      riseMeans: 'negative',
      source: { kind: 'unmapped' }
    }
  ],
  financial_health: [
    {
      key: 'income_monthly',
      label: 'Income (Monthly)',
      format: 'currency',
      riseMeans: 'positive',
      source: { kind: 'unmapped' }
    },
    {
      key: 'net_cashflow_trend',
      label: 'Net Cashflow (Monthly)',
      format: 'currency',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'C-BFSI-002' }
    },
    {
      key: 'debt_obligation_ratio',
      label: 'Debt & Obligations',
      format: 'currency',
      riseMeans: 'negative',
      source: { kind: 'metric', identifier: 'C-BFSI-004' }
    },
    {
      key: 'account_balance',
      label: 'Account Balance',
      format: 'currency',
      riseMeans: 'positive',
      source: { kind: 'unmapped' }
    },
    {
      key: 'cashflow_strength',
      label: 'Cashflow Strength',
      format: 'count',
      riseMeans: 'positive',
      source: { kind: 'unmapped' }
    },
    {
      key: 'financial_stress_signal',
      label: 'Financial Stress Score',
      format: 'points',
      riseMeans: 'negative',
      // The frame draws this gauge TWICE — here and in the Customer Profile KPI strip (`notes.md`
      // § 5.2). It is declared once, on the card whose permission the catalogue puts it behind.
      source: { kind: 'metric', identifier: 'C-BFSI-005' }
    },
    {
      key: 'income_stability_score',
      label: 'Income Stability',
      format: 'points',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'C-BFSI-001' }
    },
    {
      key: 'essential_discretionary_split',
      label: 'Essential / Discretionary Split',
      format: 'percent',
      riseMeans: 'neutral',
      source: { kind: 'metric', identifier: 'C-BFSI-003' }
    }
  ],
  product_readiness: [
    {
      // A CONDITION the catalogue reads the holding branch from, not a tile. `unmapped` for the same
      // reason `readiness_status` is: it has no catalogue metric behind it, so it is never fetched.
      key: 'credit_card_holding',
      label: 'Credit Card Holding',
      format: 'count',
      riseMeans: 'neutral',
      source: { kind: 'unmapped' }
    },
    {
      key: 'readiness_status',
      label: 'Readiness Status',
      format: 'count',
      riseMeans: 'positive',
      source: { kind: 'unmapped' }
    },
    {
      key: 'credit_card_limit_upgrade_readiness',
      label: 'Credit Card / Limit Upgrade Readiness',
      format: 'points',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'C-BFS-PR-001' }
    },
    {
      key: 'affordability_ratio',
      label: 'Affordability Ratio',
      format: 'percent',
      riseMeans: 'negative',
      source: { kind: 'unmapped' }
    },
    {
      key: 'delinquency_status',
      label: 'Delinquency Status',
      format: 'count',
      riseMeans: 'negative',
      source: { kind: 'unmapped' }
    },
    {
      key: 'personal_loan_readiness',
      label: 'Personal Loan Readiness',
      format: 'points',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'C-BFS-PR-002' }
    },
    {
      key: 'mortgage_home_loan_readiness',
      label: 'Mortgage / Home Loan Readiness',
      format: 'points',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'C-BFS-PR-003' }
    },
    {
      key: 'auto_secured_loan_readiness',
      label: 'Auto / Secured Loan Readiness',
      format: 'points',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'C-BFS-PR-004' }
    },
    {
      key: 'overdraft_credit_line_readiness',
      label: 'Overdraft / Credit Line Readiness',
      format: 'points',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'C-BFS-PR-005' }
    },
    {
      key: 'savings_account_deposit_product_readiness',
      label: 'Savings Account / Deposit Product Readiness',
      format: 'points',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'C-BFS-PR-006' }
    },
    {
      key: 'term_deposit_readiness',
      label: 'Term Deposit Readiness',
      format: 'points',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'C-BFS-PR-007' }
    },
    {
      key: 'investment_wealth_product_readiness',
      label: 'Investment / Wealth Product Readiness',
      format: 'points',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'C-BFS-PR-008' }
    },
    {
      key: 'insurance_cross_sell_readiness',
      label: 'Insurance Cross-sell Readiness',
      format: 'points',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'C-BFS-PR-009' }
    },
    /* Fintech / Wallet, `C-FIN-PR-001`…`009` — the same Phase-1 catalogue read for the third
       industry. Declared here for the reason the insurance block below states: one card, one
       selector, the tenant's industry decides which products the dropdown offers. PLA-8828. */
    {
      key: 'wallet_activation_reactivation_readiness',
      label: 'Wallet Activation / Reactivation Readiness',
      format: 'points',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'C-FIN-PR-001' }
    },
    {
      key: 'wallet_top_up_readiness',
      label: 'Wallet Top-up Readiness',
      format: 'points',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'C-FIN-PR-002' }
    },
    {
      key: 'bill_payment_adoption_readiness',
      label: 'Bill Payment Adoption Readiness',
      format: 'points',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'C-FIN-PR-003' }
    },
    {
      key: 'p2p_p2m_qr_adoption_readiness',
      label: 'P2P / P2M / QR Adoption Readiness',
      format: 'points',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'C-FIN-PR-004' }
    },
    {
      key: 'wallet_linked_card_readiness',
      label: 'Wallet-linked Card Readiness',
      format: 'points',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'C-FIN-PR-005' }
    },
    {
      key: 'bnpl_credit_readiness',
      label: 'BNPL / Credit Readiness',
      format: 'points',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'C-FIN-PR-006' }
    },
    {
      key: 'savings_investment_feature_readiness',
      label: 'Savings / Investment Feature Readiness',
      format: 'points',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'C-FIN-PR-007' }
    },
    {
      key: 'insurance_protection_feature_readiness',
      label: 'Insurance / Protection Feature Readiness',
      format: 'points',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'C-FIN-PR-008' }
    },
    {
      key: 'merchant_business_services_readiness',
      label: 'Merchant / Business Services Readiness',
      format: 'points',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'C-FIN-PR-009' }
    },
    /* Insurance, `C-INS-PR-001`…`010` — Phase 1 for the Insurance industry in the same catalogue
       (`PLA-8823/tech-doc` metrics mapping). Declared on this one card because the card is one
       selector over every industry's products; a tenant's industry decides which of them the
       dropdown offers, never which of them exist. PLA-8828. */
    {
      key: 'policy_renewal_readiness',
      label: 'Policy Renewal Readiness',
      format: 'points',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'C-INS-PR-001' }
    },
    {
      key: 'coverage_top_up_readiness',
      label: 'Coverage Top-up Readiness',
      format: 'points',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'C-INS-PR-002' }
    },
    {
      key: 'rider_add_on_readiness',
      label: 'Rider Add-on Readiness',
      format: 'points',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'C-INS-PR-003' }
    },
    {
      key: 'life_insurance_cross_sell_readiness',
      label: 'Life Insurance Cross-sell Readiness',
      format: 'points',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'C-INS-PR-004' }
    },
    {
      key: 'health_insurance_cross_sell_readiness',
      label: 'Health Insurance Cross-sell Readiness',
      format: 'points',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'C-INS-PR-005' }
    },
    {
      key: 'motor_insurance_cross_sell_readiness',
      label: 'Motor Insurance Cross-sell Readiness',
      format: 'points',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'C-INS-PR-006' }
    },
    {
      key: 'travel_insurance_readiness',
      label: 'Travel Insurance Readiness',
      format: 'points',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'C-INS-PR-007' }
    },
    {
      key: 'home_property_insurance_readiness',
      label: 'Home / Property Insurance Readiness',
      format: 'points',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'C-INS-PR-008' }
    },
    {
      key: 'premium_payment_plan_readiness',
      label: 'Premium Payment Plan Readiness',
      format: 'points',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'C-INS-PR-009' }
    },
    {
      key: 'lapse_prevention_service_recovery_readiness',
      label: 'Lapse Prevention / Service Recovery Readiness',
      format: 'points',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'C-INS-PR-010' }
    },
    /* Telco, `C-TEL-PR-001`…`008` — the fourth and last industry of the catalogue. PLA-8828. */
    {
      key: 'mobile_plan_upgrade_readiness',
      label: 'Mobile Plan Upgrade Readiness',
      format: 'points',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'C-TEL-PR-001' }
    },
    {
      key: 'data_pack_add_on_readiness',
      label: 'Data Pack / Add-on Readiness',
      format: 'points',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'C-TEL-PR-002' }
    },
    {
      key: 'roaming_pack_readiness',
      label: 'Roaming Pack Readiness',
      format: 'points',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'C-TEL-PR-003' }
    },
    {
      key: 'device_upgrade_financing_readiness',
      label: 'Device Upgrade / Financing Readiness',
      format: 'points',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'C-TEL-PR-004' }
    },
    {
      key: 'family_multi_line_plan_readiness',
      label: 'Family / Multi-line Plan Readiness',
      format: 'points',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'C-TEL-PR-005' }
    },
    {
      key: 'broadband_fibre_readiness',
      label: 'Broadband / Fibre Readiness',
      format: 'points',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'C-TEL-PR-006' }
    },
    {
      key: 'content_ott_bundle_readiness',
      label: 'Content / OTT Bundle Readiness',
      format: 'points',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'C-TEL-PR-007' }
    },
    {
      key: 'retention_renewal_offer_readiness',
      label: 'Retention / Renewal Offer Readiness',
      format: 'points',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'C-TEL-PR-008' }
    }
  ],
  cash_usage_dependency: [
    {
      key: 'cash_withdrawal_dependency_score',
      label: 'Cash Withdrawal Dependency Score',
      format: 'points',
      riseMeans: 'negative',
      source: { kind: 'metric', identifier: 'C-HYB-005' }
    },
    {
      key: 'cash_dependency_score',
      label: 'Cash Dependency Score',
      format: 'points',
      riseMeans: 'negative',
      source: { kind: 'unmapped' }
    },
    {
      key: 'cash_withdrawals',
      label: 'Cash Withdrawals',
      format: 'currency',
      riseMeans: 'negative',
      source: { kind: 'unmapped' }
    },
    {
      key: 'post_income_withdrawals',
      label: 'Post-Income Withdrawals',
      format: 'currency',
      riseMeans: 'negative',
      source: { kind: 'unmapped' }
    },
    {
      key: 'impact_on_insights',
      label: 'Impact on Insights',
      format: 'count',
      riseMeans: 'negative',
      source: { kind: 'unmapped' }
    }
  ],
  payment_merchant_behaviour: [
    {
      key: 'top_merchant_affinity',
      label: 'Top Merchant Affinity',
      format: 'percent',
      riseMeans: 'positive',
      source: { kind: 'unmapped' }
    },
    {
      key: 'category_affinity_reward_preference',
      label: 'Top Spend Category',
      format: 'percent',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'C-HYB-008' }
    },
    {
      key: 'average_spend',
      label: 'Average Spend',
      format: 'currency',
      riseMeans: 'positive',
      source: { kind: 'unmapped' }
    },
    {
      key: 'qr_payment_ratio',
      label: 'QR Payments',
      format: 'percent',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'C-HYB-004' }
    },
    {
      key: 'p2p_p2m_classification_confidence',
      label: 'P2P / P2M Confidence',
      format: 'percent',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'C-HYB-007' }
    },
    {
      key: 'spend_vs_active_user_average',
      label: 'Active User Avg Spend',
      format: 'currency',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'C-HYB-011' }
    },
    {
      key: 'transfer_vs_spend_ratio',
      label: 'Transfers vs Spend',
      format: 'percent',
      riseMeans: 'neutral',
      source: { kind: 'metric', identifier: 'C-HYB-006' }
    },
    {
      key: 'merchant_alias_resolution_profile',
      label: 'Alias Resolution',
      format: 'percent',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'C-HYB-009' }
    }
  ],
  transaction_data_quality: [
    {
      key: 'merchant_identification_confidence',
      label: 'Merchant Identification Rate',
      format: 'percent',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'C-HYB-001' }
    },
    {
      key: 'category_classification_coverage',
      label: 'Category Classification Coverage',
      format: 'percent',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'C-HYB-002' }
    },
    {
      key: 'unclassified_transaction_rate',
      label: 'Unclassified Transaction Rate',
      format: 'percent',
      riseMeans: 'negative',
      source: { kind: 'metric', identifier: 'C-HYB-003' }
    },
    {
      key: 'merchant_alias_match_rate',
      label: 'Merchant Alias Match Rate',
      format: 'percent',
      riseMeans: 'positive',
      source: { kind: 'unmapped' }
    },
    {
      key: 'gateway_masking_resolution_rate',
      label: 'Gateway Masking Resolution Rate',
      format: 'percent',
      riseMeans: 'positive',
      source: { kind: 'unmapped' }
    }
  ],
  /* The tenant-industry band. Every label is the catalogue's own `Metric / Display Label` and every
     format its own `Information Format` row — read PER METRIC, not per card: three of these sixteen
     rows state a format their card's first row does not (`C-FIN-002` Trend KPI, `C-INS-005`
     Percentage / ratio KPI, `C-TEL-002` Insight/KPI). The Layer 1 `O-*` counterparts were checked
     first and none of the sixteen matched: those are portfolio rates over the customer base
     (`Wallet Active User Rate`, `Policy Active Rate`, `Telco Churn Risk Rate`) where these are one
     customer's own reading, so the catalogue wins on every row. `riseMeans` is the metric's semantic
     direction rather than a drawn hue — Frame A draws no industry band to read one off. PLA-8828. */
  payment_usage_risk: [
    {
      key: 'payment_diversity',
      label: 'Payment Diversity',
      // A count of rails, not an index: the catalogue's example reads `4 active payment rails`.
      format: 'count',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'C-FIN-003' }
    },
    {
      key: 'wallet_velocity_risk',
      label: 'Wallet Velocity Risk',
      format: 'points',
      riseMeans: 'negative',
      source: { kind: 'metric', identifier: 'C-FIN-005' }
    }
  ],
  wallet_activity_adoption: [
    {
      key: 'wallet_activity_score',
      label: 'Wallet Activity Score',
      format: 'points',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'C-FIN-001' }
    },
    {
      key: 'wallet_balance_trend',
      label: 'Wallet Balance Trend',
      // Its `Information to Display` opens on `current balance`, so the reading is money and the
      // movement beside it is the trend the format names.
      format: 'currency',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'C-FIN-002' }
    },
    {
      key: 'next_feature_propensity',
      label: 'Next Feature Propensity',
      // `ratio` and not `points`: the example reads `0.78`, and `points` rounds a 0-1 model score to
      // one decimal where `ratio` prints it as sent.
      format: 'ratio',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'C-FIN-004' }
    }
  ],
  policy_health_claims: [
    {
      key: 'policy_status_health',
      label: 'Policy Status / Health',
      // A `Text status badge` reading — a word, not a figure. `count` is what every other
      // categorical tile on this screen takes (`lifecycle_stage`, `current_tier`, `funnel_stage`).
      format: 'count',
      /* And the word itself is the reading: `C-INS-001` states it in `value_state` and carries no
         `metric_value`, so without this the tile formatted a `null` and drew the empty-reading
         placeholder over a live `Active`. The one identity in either registry that declares it —
         `lifecycle_stage` and `current_tier` are read straight off `valueState` by
         `CustomerProfileSection` rather than drawn as tiles, and `C-COM-010`'s `funnel_stage`
         deliberately prints the ordinal its `metric_value` carries. PLA-8828. */
      reading: 'categorical',
      riseMeans: 'neutral',
      source: { kind: 'metric', identifier: 'C-INS-001' }
    },
    {
      key: 'premium_payment_risk',
      label: 'Premium Payment Risk',
      format: 'points',
      riseMeans: 'negative',
      source: { kind: 'metric', identifier: 'C-INS-002' }
    },
    {
      key: 'claim_experience_score',
      label: 'Claim Experience Score',
      format: 'points',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'C-INS-004' }
    }
  ],
  renewal_coverage_opportunities: [
    {
      key: 'renewal_risk',
      label: 'Renewal Risk',
      format: 'points',
      riseMeans: 'negative',
      source: { kind: 'metric', identifier: 'C-INS-003' }
    },
    {
      key: 'coverage_gap_signal',
      label: 'Coverage Gap Signal',
      format: 'percent',
      // A gap is an OPPORTUNITY on this card, which is why Layer 1 marks `O-INS-005` the same way.
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'C-INS-005' }
    },
    {
      key: 'rider_readiness_band',
      label: 'Rider Readiness Band',
      format: 'points',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'C-INS-006' }
    }
  ],
  churn_service_experience: [
    {
      key: 'service_experience_risk',
      label: 'Service Experience Risk',
      format: 'points',
      riseMeans: 'negative',
      source: { kind: 'metric', identifier: 'C-TEL-004' }
    },
    {
      key: 'telco_churn_risk',
      label: 'Telco Churn Risk',
      format: 'points',
      riseMeans: 'negative',
      source: { kind: 'metric', identifier: 'C-TEL-005' }
    }
  ],
  plan_usage_upgrade_readiness: [
    {
      key: 'plan_utilization',
      label: 'Plan Utilization',
      format: 'percent',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'C-TEL-001' }
    },
    {
      key: 'recharge_gap_days',
      label: 'Recharge Gap Days',
      format: 'count',
      riseMeans: 'negative',
      source: { kind: 'metric', identifier: 'C-TEL-002' }
    },
    {
      key: 'upgrade_readiness_band',
      label: 'Upgrade Readiness Band',
      format: 'points',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'C-TEL-003' }
    }
  ],
  contact_restrictions: [
    {
      key: 'do_not_nudge_state',
      label: 'Contactability Status',
      format: 'count',
      riseMeans: 'neutral',
      // `calculated` / `value_state: 'not_blocked'` with no `metric_value` -- the J-09 state IS the
      // reading, and reading it as a failure told the operator the opposite of what it says.
      reading: 'categorical',
      source: { kind: 'metric', identifier: 'C-COM-021' }
    }
  ],
  customer_opportunities_product_interest: [
    {
      key: 'active_opportunity_signals',
      label: 'Active Opportunities',
      format: 'count',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'C-COM-018' }
    },
    {
      key: 'eligible_opportunities',
      label: 'Eligible Opportunities',
      format: 'count',
      riseMeans: 'positive',
      source: { kind: 'unmapped' }
    },
    {
      key: 'product_level_propensity_score',
      label: 'Top Opportunity Propensity',
      format: 'ratio',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'C-COM-019' }
    }
  ],
  ready_for_engagement: [
    {
      key: 'nudge_readiness_score',
      label: 'Readiness Score',
      format: 'points',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'C-COM-020' }
    },
    {
      key: 'handoff_status',
      label: 'Status',
      format: 'count',
      riseMeans: 'positive',
      source: { kind: 'unmapped' }
    }
  ]
};

/**
 * A card's tiles, narrowed to the tenant's industry off `productMetricKeysFor` — the same source of
 * truth `ProductReadinessSection.productOptionsOf` narrows the dropdown with, so the fetch and the
 * selector cannot drift. Product Readiness declares all four industries' products, so without this
 * every tenant asked for all 36 `C-*-PR-*` and took 27 403s back. An unrecognised slug narrows
 * nothing: a tenant we cannot place must not silently lose its card. PLA-8828.
 */
export const customer360MetricIdentitiesFor = (
  cardKey: string,
  industry?: string | null
): MetricIdentity[] => {
  const entries = Object.prototype.hasOwnProperty.call(customer360MetricIdentities, cardKey)
    ? customer360MetricIdentities[cardKey]
    : [];

  if (!isIndustrySlug(industry)) {
    return entries;
  }

  const ofThisIndustry = new Set(productMetricKeysFor(industry));

  return entries.filter(entry => !isProductMetricKey(entry.key) || ofThisIndustry.has(entry.key));
};

/**
 * Every catalogue metric a Customer 360 card needs a reading from, deduplicated — the fetch set a
 * permitted card issues, and the provisioning ask for any id that does not yet answer.
 *
 * No `derived` arm, unlike Layer 1's `liveIdentifiers`: every C360 tile either IS a catalogue metric
 * or has no catalogue mapping at all.
 */
/**
 * Layer 1's `primaryMetricIdFor` for the Customer 360 catalogue: the first id the card's tiles name,
 * or `null` where they name none. Orbit sends it as `context.metric_id` for the card in view
 * (PLA-8844). Kept here rather than parameterised on Layer 1's because the two registries are
 * separate on purpose — four card keys mean different cards across the two screens.
 */
export const customer360PrimaryMetricIdFor = (cardKey: string): string | null =>
  customer360MetricIdentitiesFor(cardKey).flatMap(entry => identifiersNamedBy(entry.source))[0] ??
  null;

export const customer360LiveIdentifiers = (): string[] =>
  Array.from(
    new Set(
      Object.values(customer360MetricIdentities)
        .flat()
        .filter(entry => entry.source.kind === 'metric')
        .map(entry => (entry.source as { identifier: string }).identifier)
    )
  ).sort();
